import type { AuthProvider } from "@refinedev/core";
import { hasSupabaseConfig } from "../lib/env";
import { fetchWhoami, StaffForbiddenError } from "../lib/staff";
import { getSupabaseClient } from "../lib/supabase";

export const authProvider: AuthProvider = {
	login: async ({ email, password }) => {
		if (!hasSupabaseConfig()) {
			return {
				success: false,
				error: {
					name: "ConfigError",
					message: "VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY が未設定です。",
				},
			};
		}

		const { error } = await getSupabaseClient().auth.signInWithPassword({
			email: String(email ?? "").trim(),
			password: String(password ?? ""),
		});

		if (error) {
			return {
				success: false,
				error: {
					name: "LoginError",
					message: error.message,
				},
			};
		}

		try {
			await fetchWhoami();
		} catch (err) {
			await getSupabaseClient().auth.signOut();
			if (err instanceof StaffForbiddenError) {
				return {
					success: false,
					error: {
						name: "Forbidden",
						message: err.message,
					},
				};
			}
			return {
				success: false,
				error: {
					name: "StaffCheckError",
					message:
						err instanceof Error
							? err.message
							: "スタッフ権限の確認に失敗しました。",
				},
			};
		}

		return {
			success: true,
			redirectTo: "/",
		};
	},

	logout: async () => {
		const { error } = await getSupabaseClient().auth.signOut();
		if (error) {
			return {
				success: false,
				error: {
					name: "LogoutError",
					message: error.message,
				},
			};
		}
		return {
			success: true,
			redirectTo: "/login",
		};
	},

	check: async () => {
		if (!hasSupabaseConfig()) {
			return {
				authenticated: false,
				redirectTo: "/login",
				error: {
					name: "ConfigError",
					message: "VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY が未設定です。",
				},
			};
		}

		const { data, error } = await getSupabaseClient().auth.getSession();
		if (error || !data.session) {
			return {
				authenticated: false,
				redirectTo: "/login",
			};
		}

		try {
			await fetchWhoami();
			return { authenticated: true };
		} catch (err) {
			if (err instanceof StaffForbiddenError) {
				return {
					authenticated: false,
					redirectTo: "/forbidden",
					error: {
						name: "Forbidden",
						message: err.message,
					},
				};
			}
			return {
				authenticated: false,
				redirectTo: "/login",
				error: {
					name: "StaffCheckError",
					message:
						err instanceof Error
							? err.message
							: "スタッフ権限の確認に失敗しました。",
				},
			};
		}
	},

	getIdentity: async () => {
		try {
			const whoami = await fetchWhoami();
			return {
				id: whoami.userId,
				name: whoami.email ?? whoami.userId,
				email: whoami.email,
				role: whoami.role,
			};
		} catch {
			return null;
		}
	},

	getPermissions: async () => {
		try {
			const whoami = await fetchWhoami();
			return whoami.role;
		} catch {
			return null;
		}
	},

	onError: async (error) => {
		console.error("refine auth onError:", error);
		return {};
	},
};
